/**
 * @file components/log/DietPlanModal.jsx
 * @description Refactored to use TanStack Query mutations and optimized state management.
 */

import { useMutation } from "@tanstack/react-query";
import { AlertCircle, Calculator, Camera, Loader2, Save, X } from "lucide-react";
import { memo, useRef, useState } from "react";

import { estimateNutrition } from "../../services/calorieApi";
import { useAuth } from "../../utils/AuthContext";

const MAX_INPUT_LENGTH = 250;

const EntryMealModal = memo(
  ({
    onSave,
    onEdit,
    onClose,
    isDietPlan = true,
    meal = {},
    isEdit = false,
  }) => {
    const { user } = useAuth();
    const uid = user?.uid;

    // --- STATE ---
    const [title, setTitle] = useState(meal?.label || "");
    const [description, setDescription] = useState(meal?.description || "");
    const [analysis, setAnalysis] = useState(null);
    const [image, setImage] = useState(null);
    const [showPhotoOptions, setShowPhotoOptions] = useState(false);
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);

    // --- IMAGE HANDLING ---
    function handleImageSelect(e) {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!file.type.startsWith("image/")) return;
      if (file.size > 5 * 1024 * 1024) return;

      const img = new Image();
      img.onload = () => {
        const MAX = 768;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          const scale = MAX / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
        setImage({ base64: dataUrl.split(",")[1], mimeType: "image/jpeg", preview: dataUrl });
        if (analysis) setAnalysis(null);
        URL.revokeObjectURL(img.src);
      };
      img.src = URL.createObjectURL(file);
    }

    function removeImage() {
      setImage(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }

    // --- MUTATIONS ---

    // 1. Analysis Mutation
    const analysisMutation = useMutation({
      mutationFn: ({ text, img }) => estimateNutrition(text, img),
      onSuccess: (data) => setAnalysis(data),
    });

    // 2. Save/Edit Mutation
    const actionMutation = useMutation({
      mutationFn: async (newPlanData) => {
        if (isEdit) {
          return onEdit(meal?.id, newPlanData, description.trim());
        }
        return onSave(newPlanData, description.trim());
      },
      onSuccess: () => onClose(),
    });

    // --- HANDLERS ---
    const handleAnalyse = () => {
      if (!description.trim() && !image) return;
      analysisMutation.mutate({
        text: description.trim() || "",
        img: image ? { base64: image.base64, mimeType: image.mimeType } : null,
      });
    };

    const handleSave = () => {
      if ((isDietPlan && !title) || !analysis || !uid) return;

      const newPlanData = {
        label: title || "Custom Meal",
        emoji: "🍱",
        items: (analysis.items || []).map((item) => ({
          name: item.name || "Unknown Item",
          cal: Number(item.cal || 0),
          pro: Number(item.pro || 0),
        })),
        totalCal: Number(analysis.totalCal || 0),
        totalPro: Number(analysis.totalPro || 0),
      };

      actionMutation.mutate(newPlanData);
    };

    const isLoading = analysisMutation.isPending || actionMutation.isPending;
    const error =
      analysisMutation.error?.message || actionMutation.error?.message;

    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        {/* Overlay */}
        <div
          className="absolute inset-0 bg-black/80 backdrop-blur-md"
          onClick={onClose}
        />

        <div className="relative bg-[#0F1115] border border-white/10 w-full max-w-md rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
          {/* Header */}
          <div className="p-8 border-b border-white/5 flex justify-between items-center bg-white/[0.01]">
            <h3 className="text-xl font-bold text-white tracking-tight font-serif">
              {isEdit ? "Edit Entry" : "New Entry"}
            </h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/5 rounded-full text-gray-500 transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          <div className="p-8 space-y-6">
            {isDietPlan && (
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest ml-1">
                  Meal Title
                </label>
                <input
                  type="text"
                  placeholder="Breakfast, Snack..."
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full bg-white/[0.03] border border-white/10 rounded-2xl px-5 py-4 text-white placeholder-gray-600 focus:border-brand-green/40 outline-none transition-all"
                />
              </div>
            )}

            <div className="space-y-2">
              <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest ml-1">
                Foods & Portions
              </label>
              <textarea
                rows={4}
                placeholder="e.g. 3 eggs omelet, tomato slices..."
                value={description}
                maxLength={MAX_INPUT_LENGTH}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length <= MAX_INPUT_LENGTH) {
                    setDescription(val);
                    if (analysis) setAnalysis(null);
                    if (analysisMutation.isError) analysisMutation.reset();
                  }
                }}
                className="w-full bg-white/[0.03] border border-white/10 rounded-2xl px-5 py-4 text-white placeholder-gray-600 focus:border-brand-green/40 outline-none resize-none transition-all leading-relaxed"
              />
              <div className="flex justify-end">
                <span className={`text-[10px] font-medium ${description.length >= MAX_INPUT_LENGTH ? 'text-red-400' : 'text-gray-600'}`}>
                  {description.length}/{MAX_INPUT_LENGTH}
                </span>
              </div>

              {/* Photo Upload */}
              <div className="relative flex items-center gap-3 pt-1">
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />
                <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleImageSelect} className="hidden" />
                <button
                  type="button"
                  onClick={() => setShowPhotoOptions(!showPhotoOptions)}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl border border-white/10 bg-white/[0.03] text-gray-300 text-xs font-medium hover:border-brand-green/40 hover:text-brand-green transition-all"
                >
                  <Camera size={14} />
                  {image ? "Change Photo" : "Add Photo"}
                </button>

                {showPhotoOptions && (
                  <div className="absolute top-full left-0 mt-2 z-50 bg-[#1a1a1a] border border-white/10 rounded-xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
                    <button
                      onClick={() => { cameraInputRef.current?.click(); setShowPhotoOptions(false); }}
                      className="w-full flex items-center gap-3 px-5 py-3 text-xs text-gray-200 hover:bg-white/5 transition-colors text-left"
                    >
                      <Camera size={14} className="text-emerald-400" />
                      Take Photo
                    </button>
                    <button
                      onClick={() => { fileInputRef.current?.click(); setShowPhotoOptions(false); }}
                      className="w-full flex items-center gap-3 px-5 py-3 text-xs text-gray-200 hover:bg-white/5 transition-colors text-left border-t border-white/5"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-400"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
                      Photo Library
                    </button>
                  </div>
                )}

                {!image && !showPhotoOptions && <span className="text-[10px] text-gray-600">Optional — add a photo for better accuracy</span>}
              </div>

              {image && (
                <div className="relative inline-block">
                  <img src={image.preview} alt="Meal" className="w-24 h-24 object-cover rounded-xl border border-white/10" />
                  <button type="button" onClick={removeImage} className="absolute -top-2 -right-2 p-1 rounded-full bg-red-500 text-white hover:bg-red-400 transition-colors"><X size={10} /></button>
                </div>
              )}
            </div>

            {/* Analysis Result Card */}
            {analysis && (
              <div className="bg-brand-green/[0.03] rounded-3xl p-5 border border-brand-green/20 flex justify-between items-center animate-in fade-in slide-in-from-top-2">
                <div>
                  <p className="text-[10px] text-brand-green/70 uppercase font-bold tracking-widest mb-1">
                    Total Energy
                  </p>
                  <p className="text-brand-green font-bold text-2xl font-sans">
                    {analysis.totalCal}{" "}
                    <span className="text-xs font-medium opacity-50">kcal</span>
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-amber-500/70 uppercase font-bold tracking-widest mb-1">
                    Protein
                  </p>
                  <p className="text-amber-400 font-bold text-2xl font-sans">
                    {analysis.totalPro}g
                  </p>
                </div>
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 text-red-400 text-xs bg-red-400/10 p-4 rounded-2xl border border-red-400/20">
                <AlertCircle size={14} /> {error}
              </div>
            )}

            {/* Footer Actions */}
            <div className="flex flex-col gap-3 pt-2">
              {!analysis ? (
                <button
                  onClick={handleAnalyse}
                  disabled={isLoading || (!description.trim() && !image)}
                  className="w-full flex items-center justify-center gap-3 py-5 rounded-2xl bg-brand-green text-[#0F1115] font-bold text-sm uppercase tracking-widest hover:bg-green-400 transition-all active:scale-[0.98] disabled:opacity-20 shadow-[0_0_20px_rgba(16,185,129,0.15)]"
                >
                  {isLoading ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    <Calculator size={18} />
                  )}
                  {isLoading ? "Calculating..." : "Calculate Nutrition"}
                </button>
              ) : (
                <button
                  onClick={handleSave}
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-3 py-5 rounded-2xl bg-brand-green text-[#0F1115] font-bold text-sm uppercase tracking-widest hover:bg-green-400 transition-all active:scale-[0.98] shadow-[0_0_20px_rgba(16,185,129,0.2)]"
                >
                  {actionMutation.isPending ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    <Save size={18} />
                  )}
                  {actionMutation.isPending
                    ? "Saving..."
                    : isEdit
                      ? "Update Entry"
                      : "Save Entry"}
                </button>
              )}

              {analysis && (
                <button
                  onClick={() => setAnalysis(null)}
                  className="text-[10px] text-gray-500 uppercase tracking-widest font-bold hover:text-gray-300 transition-colors"
                >
                  Reset Calculation
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  },
);

EntryMealModal.displayName = "DietPlanModal";

export default EntryMealModal;
